import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { RootState } from '../ApplicationState';
import { selectClassroom } from '../Classrooms';
import TailwindButton from '../components/TailwindButton';
import TypeformEmbed from '../Survey/SurveyEmbed';
import { createSurveySession } from '../BackendInterface';
import { TAKWEEN_SURVEY_FORM_ID } from './constants';
import { TakweenSurveyRouteState } from './types';
import { useDirectSurveyCompletions } from './useDirectSurveyCompletions';

interface SurveyParams {
  classroomId: string;
  studentId: string;
}

const TakweenStudentSurveyPage: React.FC = () => {
  const classroom = useSelector((state: RootState) => selectClassroom(state));
  const userProfile = useSelector((state: RootState) => state.user.userProfile);
  const history = useHistory();
  const location = useLocation<TakweenSurveyRouteState>();
  const { classroomId, studentId } = useParams<SurveyParams>();
  const [selectedLanguage, setSelectedLanguage] = useState<'en' | 'ar'>('ar');
  const [sessionId, setSessionId] = useState('');
  const [isLoadingSession, setIsLoadingSession] = useState(true);
  const [sessionError, setSessionError] = useState<string | null>(null);

  const student = classroom?.listOfStudentProxies?.find((item) => item.userId === studentId);
  const {
    completedStudentUserIdSet,
    isLoading: isLoadingCompletions,
    error: completionsError,
  } = useDirectSurveyCompletions({
    classroomId,
    formId: TAKWEEN_SURVEY_FORM_ID,
  });
  const isCompleted = completedStudentUserIdSet.has(studentId);

  const teacherName = `${userProfile?.firstName || ''} ${userProfile?.lastName || ''}`.trim();
  const studentName = `${student?.firstName || ''} ${student?.lastName || ''}`.trim();

  useEffect(() => {
    const initializeSurveySession = async () => {
      if (!classroom || !student || isLoadingCompletions) {
        return;
      }

      if (isCompleted) {
        setIsLoadingSession(false);
        return;
      }

      if (sessionId) {
        setIsLoadingSession(false);
        return;
      }

      if (!userProfile?.userId) {
        setSessionError('Unable to initialize survey session. Please refresh and try again.');
        setIsLoadingSession(false);
        return;
      }

      try {
        setIsLoadingSession(true);
        setSessionError(null);

        const response = await createSurveySession({
          type: 'direct',
          params: {
            formId: TAKWEEN_SURVEY_FORM_ID,
            teachersUserId: userProfile.userId,
            teachersName: teacherName,
            studentsUserId: studentId,
            studentsName: studentName,
            classroomId,
            className: classroom.className || '',
            district: classroom.district || userProfile.district || '',
            schoolId: userProfile.schoolId || classroom.schoolId || '',
          },
        });

        setSessionId(response.sessionId);
      } catch (error) {
        console.error('Error creating Takween survey session:', error);
        setSessionError('Failed to initialize survey session. Please try again.');
      } finally {
        setIsLoadingSession(false);
      }
    };

    void initializeSurveySession();
  }, [
    classroom,
    classroomId,
    isCompleted,
    isLoadingCompletions,
    sessionId,
    student,
    studentId,
    studentName,
    teacherName,
    userProfile,
  ]);

  const hiddenFields = useMemo(
    () => ({
      session_id: sessionId,
      teachers_user_id: userProfile?.userId || '',
      student_id: studentId,
      classroom_id: classroomId,
      district: classroom?.district || userProfile?.district || '',
      school_id: userProfile?.schoolId || classroom?.schoolId || '',
    }),
    [classroom?.district, classroom?.schoolId, classroomId, sessionId, studentId, userProfile],
  );

  const handleSurveySubmit = () => {
    history.push('/takween/survey/confirmation', {
      ...location.state,
      classroomId,
      justCompletedStudentId: studentId,
    });
  };

  if (!classroom || !student) {
    return (
      <div className='min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8'>
        <div className='mx-auto mt-8 max-w-lg rounded-lg bg-white p-8 text-center shadow-sm'>
          <h2 className='m-0 text-[2.6rem] text-gray-800'>Student not found</h2>
          <p className='mb-6 mt-4 text-[1.6rem] text-gray-500'>Return to the student list and try again.</p>
          <TailwindButton variant='secondary' onClick={() => history.push('/takween/survey/students', location.state)}>
            Back to Student List
          </TailwindButton>
        </div>
      </div>
    );
  }

  if (isCompleted) {
    return (
      <div className='min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8'>
        <div className='mx-auto mt-8 max-w-lg rounded-lg bg-white p-8 text-center shadow-sm'>
          <h2 className='m-0 text-[2.6rem] text-gray-800'>Survey already completed</h2>
          <p className='mb-6 mt-4 text-[1.6rem] text-gray-500'>{studentName} has already completed this survey.</p>
          <TailwindButton variant='secondary' onClick={() => history.push('/takween/survey/students', location.state)}>
            Back to Student List
          </TailwindButton>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gray-50 p-4 md:p-6 lg:p-8'>
      <div className='mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
        <div>
          <h1 className='m-0 text-[3rem] font-bold text-gray-800'>Student Post-Survey</h1>
          <p className='mt-1.5 text-[2rem] text-gray-500'>{studentName}</p>
        </div>
        <div className='flex flex-wrap gap-4'>
          <TailwindButton variant='secondary' onClick={() => history.push('/takween/survey/students', location.state)}>
            Back to Student List
          </TailwindButton>
          <TailwindButton
            variant='secondary'
            className='min-w-[220px]'
            onClick={() => setSelectedLanguage((current) => (current === 'en' ? 'ar' : 'en'))}
          >
            Switch to {selectedLanguage === 'en' ? 'Arabic' : 'English'}
          </TailwindButton>
        </div>
      </div>

      <div className='mb-4 inline-flex items-center rounded-full bg-indigo-50 px-4 py-1.5 text-[1.4rem] font-semibold text-gray-800'>
        Language: {selectedLanguage === 'en' ? 'English' : 'Arabic'}
      </div>

      <div className='rounded-lg bg-white p-4 shadow-sm'>
        {isLoadingCompletions ? (
          <div className='py-8 text-center'>
            <div className='mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-blue-500'></div>
            <p className='mt-4 text-[1.6rem] text-gray-500'>Checking completion status...</p>
          </div>
        ) : isLoadingSession ? (
          <div className='py-8 text-center'>
            <div className='mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-blue-500'></div>
            <p className='mt-4 text-[1.6rem] text-gray-500'>Preparing survey session...</p>
          </div>
        ) : sessionError ? (
          <div className='py-8 text-center'>
            <p className='text-[1.6rem] text-red-600'>{sessionError}</p>
          </div>
        ) : (
          <>
            {completionsError && (
              <p className='mb-4 text-center text-[1.4rem] text-amber-700'>
                Completion status is temporarily unavailable. You can still continue.
              </p>
            )}
            <TypeformEmbed
              key={`${selectedLanguage}-${sessionId}`}
              formId={TAKWEEN_SURVEY_FORM_ID}
              minHeight='680px'
              hiddenFields={hiddenFields}
              tracking={{
                'typeform-lang': selectedLanguage,
              }}
              onSubmit={handleSurveySubmit}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default TakweenStudentSurveyPage;
